import { gql } from "@/gql/__generated__";
import {
  ApiKeyFragmentFragment,
  AppApiKeysQuery,
  SubscriptionAppApiKeysQuery
} from "@/gql/__generated__/graphql";
import { QueryResult, useQuery } from "@apollo/client";
import { useUserActiveOrganization } from "../user/useUserActiveOrganization";

export const APP_API_KEYS = gql(`
  query appAPIKeys($organizationSlug: String!, $appSlug: String!) {
    app(organizationSlug: $organizationSlug, appSlug: $appSlug) {
      __typename
      id
      defaultDeployment {
        __typename
        id
        apiKeys {
          ...APIKeyFragment
        }
      }
    }
  }

  fragment APIKeyFragment on AppAPIKey {
    __typename
    id
    name
    secret
  }
`);

export const SUBSCRIPTION_APP_API_KEYS = gql(`
  query subscriptionAppAPIKeys($subscriptionId: ID!) {
    subscription(subscriptionId: $subscriptionId) {
      __typename
      id
      apiKeys {
        ...APIKeyFragment
      }
    }
  }
`);

type APIKeyQueryReturn = Omit<
  QueryResult<AppApiKeysQuery | SubscriptionAppApiKeysQuery>,
  "data"
> & {
  data: ApiKeyFragmentFragment[] | undefined;
};

export enum Origin {
  Organization,
  Subscription
}

const originMap = {
  [Origin.Organization]: APP_API_KEYS,
  [Origin.Subscription]: SUBSCRIPTION_APP_API_KEYS
} as const;

interface Props {
  appSlug: string | undefined;
  subscriptionId: string | undefined;
  origin: Origin | undefined;
}

export function useAppAPIKeys({
  appSlug,
  subscriptionId,
  origin = Origin.Organization
}: Props): APIKeyQueryReturn {
  const { activeOrganization } = useUserActiveOrganization();

  const { data, ...rest } = useQuery<
    AppApiKeysQuery | SubscriptionAppApiKeysQuery
  >(originMap[origin], {
    variables:
      origin === Origin.Organization
        ? {
            organizationSlug: activeOrganization?.slug || "",
            appSlug
          }
        : {
            organizationSlug: activeOrganization?.slug || "",
            subscriptionId
          },
    skip: !activeOrganization
  });

  if (!data) return { ...rest, data: undefined };

  let apiKeys: ApiKeyFragmentFragment[] = [];

  if ("app" in data)
    apiKeys =
      (data.app?.defaultDeployment?.apiKeys.filter(
        (key) => key !== null
      ) as ApiKeyFragmentFragment[]) || [];
  if ("subscription" in data)
    apiKeys =
      (data.subscription.apiKeys.filter(
        (key) => key !== null
      ) as ApiKeyFragmentFragment[]) || [];

  return { ...rest, data: apiKeys };
}
